// The race screen's rules (spec §13.5 "What the athlete sees", §13.15; M6):
// each goal with its on-track indicator and the coach's latest word on it,
// the phases to each race, and the weeks of a phase with their objectives
// and, for a week not written yet, its planned sessions. Pure, like
// timeline.js: the caller passes `today`.
//
// Where it comes from:
//   - `state.strategy.on_track`: GET /v2/strategy's latest trajectory reading
//     per goal (app/plan/trajectory.py `latest`): {goal_key, goal_date, day,
//     expected, projected, status, drift}. `goal_key` is an A race's id, or
//     "goal" for a rolling strategy;
//   - the on-track message: a coach_message with body {text, kind: "on_track",
//     status, goal} (trajectory._say), sent when the shown status changes;
//   - the phase review: a plan_update with body {text, kind: "phase_review",
//     phase, next, decision} (app/coach/head/phase_review.py `_announce`).

import { phaseOn, shownPhases, weekStartOf, addDays, windowLabel } from './timeline';
import { formatMinutes } from '../lib/duration';

const day = (iso) => String(iso).slice(0, 10);

export const ON_TRACK_STATUSES = ['on_track', 'ahead', 'behind'];

/** What each indicator state says. `missing`: no reading yet for this goal. */
export const ON_TRACK_META = {
  on_track: { label: 'On track', hint: 'Your fitness is heading where the plan needs it on race day', goalHint: 'Your fitness is heading where the plan expects' },
  ahead: { label: 'Ahead of plan', hint: 'Your fitness is heading above what the plan expected for race day', goalHint: 'Your fitness is heading above what the plan expects' },
  behind: { label: 'Behind plan', hint: 'Your fitness is heading below what the plan expected for race day', goalHint: 'Your fitness is heading below what the plan expects' },
  missing: { label: 'Not read yet', hint: 'Your coach checks this every night once there is training on the plan to read' },
};

/** The indicator's line: race day only for a race; a rolling plan's goal has none. */
export const onTrackHint = (goal) => {
  const meta = ON_TRACK_META[goal.state] || ON_TRACK_META.missing;
  return (!goal.race && meta.goalHint) || meta.hint;
};

/** A reading's state for the indicator: one of ON_TRACK_STATUSES, else 'missing'. */
export const indicatorState = (reading) => (reading && ON_TRACK_STATUSES.includes(reading.status) ? reading.status : 'missing');

export const isOnTrackMessage = (item) => item?.type === 'coach_message' && item.body?.kind === 'on_track';
export const isPhaseReview = (item) => item?.type === 'plan_update' && item.body?.kind === 'phase_review';

/** The coach's latest on-track message about `goalKey` (by seq), or null. */
export function latestOnTrackMessage(thread, goalKey) {
  let best = null;
  for (const item of Object.values(thread || {})) {
    if (!isOnTrackMessage(item) || String(item.body.goal) !== String(goalKey)) continue;
    if (!best || item.seq > best.seq) best = item;
  }
  return best;
}

/** The last day the committed strategy plans: its last phase's end, else its last week's. */
function strategyEnd(strategy) {
  const ends = [
    ...(strategy?.timeline || []).map((p) => day(p.end_date)),
    ...Object.values(strategy?.weeks || {}).map((w) => addDays(w.week_start, 6)),
  ];
  return ends.sort().pop() || null;
}

/**
 * The goals the race screen shows, by date: every A race ahead, and every
 * goal the trajectory has a reading for that is still ahead. With no A
 * race ahead (a rolling strategy) there is one goal, "goal", at the end of
 * the plan. Each:
 * {key, title, date, race, reading, state, message}.
 */
export function seasonGoals(state, today) {
  const strategy = state?.strategy;
  if (!strategy) return [];
  const races = Object.values(state.races || {});
  const raceBy = Object.fromEntries(races.map((r) => [String(r.id), r]));
  const readings = Object.fromEntries((strategy.on_track || []).map((r) => [String(r.goal_key), r]));
  const keys = new Set([
    ...races.filter((r) => r.priority === 'A' && day(r.date) >= day(today)).map((r) => String(r.id)),
    ...Object.keys(readings),
  ]);
  const out = [];
  const add = (key) => {
    const race = raceBy[key] || null;
    const reading = readings[key] || null;
    const date = race?.date ? day(race.date) : reading?.goal_date ? day(reading.goal_date) : key === 'goal' ? strategyEnd(strategy) : null;
    if (!date || date < day(today)) return;            // a goal behind the athlete has nothing to track
    if (!race && key !== 'goal') return;               // a reading for a race since removed
    out.push({
      key, date, race, reading,
      title: race?.title || state.goal?.label || 'Your goal',
      state: indicatorState(reading),
      message: latestOnTrackMessage(state.thread, key),
    });
  };
  keys.forEach(add);
  if (!out.length && !keys.has('goal')) add('goal');   // no A race ahead: the strategy's own end
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.key.localeCompare(b.key));
}

/** The phase the screen opens on: today's, else the next one, else the last. */
export function currentPhase(phases, today) {
  if (!phases.length) return null;
  const sorted = [...phases].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  return phaseOn(sorted, today) || sorted.find((p) => day(p.start_date) > day(today)) || sorted.at(-1);
}

/**
 * A phase's weeks, in order: each outline week of the committed strategy
 * with `current` (today is in it), `past`, `outline` (not written yet: its
 * planned sessions are all there is) and its planned sessions by date.
 */
export function phaseWeeks(state, phase, today) {
  if (!phase) return [];
  const thisWeek = weekStartOf(today);
  const inPhase = (w) => (w.phase_id != null ? String(w.phase_id) === String(phase.id)
    : day(w.week_start) >= day(phase.start_date) && day(w.week_start) <= day(phase.end_date));
  return Object.values(state?.strategy?.weeks || {})
    .filter(inPhase)
    .sort((a, b) => day(a.week_start).localeCompare(day(b.week_start)))
    .map((w, i, all) => ({
      ...w,
      number: i + 1,
      of: all.length,
      datesLabel: windowLabel(w.week_start, addDays(w.week_start, 6)),
      current: day(w.week_start) === thisWeek,
      past: day(w.week_start) < thisWeek,
      outline: !w.materialized,
      sessions: [...(w.sessions || [])].sort((a, b) => day(a.date).localeCompare(day(b.date))
        || Number(Boolean(b.is_key)) - Number(Boolean(a.is_key))),
    }));
}

/** Everything the race screen draws. */
export function seasonView(state, today) {
  const shown = shownPhases(state);
  const phase = currentPhase(shown.phases, today);
  return {
    hasStrategy: Boolean(state?.strategy),
    phases: shown.phases, draft: shown.draft,
    proposed: state?.draft?.stage === 'proposed',
    goals: seasonGoals(state, today),
    races: Object.values(state?.races || {}),
    phase,
    headline: seasonHeadline(state, today),
    list: seasonPhases(shown.phases, Object.values(state?.races || {}), today),
  };
}

// What a week's load intent is called on screen (LoadIntent).
export const LOAD_INTENT_LABEL = {
  build: 'Build', hold: 'Hold', deload: 'Easier week', taper: 'Taper', race: 'Race week', recover: 'Recovery',
};

/** 45 → "45 min", 90 → "1h 30m", 120 → "2h" (the one convention, lib/duration). */
export function minutesLabel(min) {
  return formatMinutes(min) || '0 min';
}

const DECISION = {
  confirm: (next) => `${next} confirmed`,
  adjust: (next) => `${next} adjusted`,
  replace: (next) => `${next} being reworked`,
};

/** The phase-review card: "Base done · Build confirmed", the coach's text, and whether a rework follows. */
export function phaseReviewView(item) {
  const b = item?.body || {};
  const done = b.phase ? `${b.phase} done` : 'Phase done';
  const next = b.next ? (DECISION[b.decision] || DECISION.confirm)(b.next) : null;
  return {
    title: [done, next].filter(Boolean).join(' · '),
    text: b.text || '',
    decision: DECISION[b.decision] ? b.decision : 'confirm',
    reworkFollows: b.decision === 'replace',
  };
}

// ---------------------------------------------------------------------------
// The season page at a glance (web SeasonScreen, and the mobile app's season
// screen uses the same structure and words): a header with the next race or
// goal; the phases in order, each with what it's for and done / now / next;
// races as labelled rows inside the phase they fall in; "You are here" on
// the current week. Every word the athlete reads is here, so both apps match.

const DAY_MS = 86400000;
const dayMs = (iso) => Date.parse(`${day(iso)}T00:00:00Z`);
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A race's priority in words: `short` on a row, `line` in the header. */
export const PRIORITY_WORDS = {
  A: { short: 'A race', line: 'A race: your main race' },
  B: { short: 'B race', line: 'B race: a target race on the way' },
  C: { short: 'C race', line: 'C race: a training race' },
};
const NO_PRIORITY = { short: 'Race', line: 'Race: priority not set yet' };
export const priorityWords = (priority) => PRIORITY_WORDS[String(priority || '').toUpperCase()] || NO_PRIORITY;

/** Where a phase stands against today. */
export const PHASE_STATUS_WORDS = { done: 'Done', now: 'Now', next: 'Next', later: 'Later' };

/** What a phase is for when the coach gave no objective, by its kind (PHASE_KINDS). */
export const PHASE_PURPOSE = {
  base: 'Steady, easy training to build your endurance',
  build: 'Harder sessions to lift your fitness',
  race_specific: 'Training that looks like your race',
  peak: 'Your most race-like weeks',
  taper: 'Less training so you arrive fresh',
  race: 'Race week',
  recovery: 'Easy weeks to recover',
  transition: 'A break before the next block',
};

/** "Sun 13 Dec"; the year too when it isn't `today`'s. */
export function raceDayLabel(iso, today = null) {
  const d = new Date(dayMs(iso));
  const year = today && d.getUTCFullYear() !== new Date(dayMs(today)).getUTCFullYear() ? ` ${d.getUTCFullYear()}` : '';
  return `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${year}`;
}

/** Whole days from `today` to `iso`; negative when it's gone by. */
export const daysBetween = (today, iso) => Math.round((dayMs(iso) - dayMs(today)) / DAY_MS);

/** "Today", "Tomorrow", "25 days to go"; null once it's gone by. */
export function daysToGoLabel(today, iso) {
  const n = daysBetween(today, iso);
  if (n < 0) return null;
  if (n === 0) return 'Today';
  if (n === 1) return 'Tomorrow';
  return `${n} days to go`;
}

/** "Week 2 of 4" */
export const weekOfLabel = (n, of) => (n && of ? `Week ${n} of ${of}` : null);
export const YOU_ARE_HERE = 'You are here';

const byDate = (a, b) => day(a.date).localeCompare(day(b.date)) || String(a.id).localeCompare(String(b.id));

/** A race as a row: {id, title, date, dateLabel, priority, short, line, toGo, past}. */
export function raceRow(race, today) {
  const words = priorityWords(race.priority);
  return {
    id: race.id, title: race.title || 'Race', date: day(race.date),
    dateLabel: raceDayLabel(race.date, today), priority: race.priority || null,
    short: words.short, line: words.line,
    toGo: daysToGoLabel(today, race.date), past: daysBetween(today, race.date) < 0,
  };
}

/**
 * What the header leads with: the next A race (what the plan builds to),
 * else the next race; `before` lists the races ahead of it. With no race
 * ahead, a goal (the caller adds GET /v2/goals/progress). Null with neither.
 */
export function seasonHeadline(state, today) {
  const ahead = Object.values(state?.races || {}).filter((r) => r?.date && daysBetween(today, r.date) >= 0).sort(byDate);
  const main = ahead.find((r) => String(r.priority).toUpperCase() === 'A') || ahead[0];
  if (main) {
    return {
      kind: 'race', race: raceRow(main, today),
      before: ahead.filter((r) => byDate(r, main) < 0).map((r) => raceRow(r, today)),
    };
  }
  return state?.strategy ? { kind: 'goal' } : null;
}

/**
 * The goal header from GET /v2/goals/progress (app/athlete/goal_progress.card):
 * {title, dateLabel, toGo, now, target, start, percent, reached, steady}.
 * A goal with no number ("Stay fit and consistent") shows its consistency.
 */
export function goalHeadline(progress, today) {
  if (!progress) return null;
  const by = progress.by_date ? day(progress.by_date) : null;
  const steady = progress.consistency?.planned
    ? `${progress.consistency.done} of ${progress.consistency.planned} sessions done this week`
      + (progress.consistency.weeks_in_a_row ? ` · ${progress.consistency.weeks_in_a_row} week${progress.consistency.weeks_in_a_row === 1 ? '' : 's'} in a row` : '')
    : null;
  return {
    title: progress.title || 'Stay fit and consistent',
    dateLabel: by ? `By ${raceDayLabel(by, today)}` : null,
    toGo: by ? daysToGoLabel(today, by) : null,
    now: progress.now || null, target: progress.target || null, start: progress.start || null,
    percent: progress.percent == null ? null : Math.max(0, Math.min(100, Number(progress.percent))),
    reached: Boolean(progress.achieved_at),
    steady,
  };
}

/** Which week of `phase` today is in, counted from the phase's first Monday; null outside it. */
export function phaseWeekNumber(phase, today) {
  if (!phase?.start_date || dayMs(today) < dayMs(phase.start_date) || dayMs(today) > dayMs(phase.end_date)) return null;
  return Math.floor((dayMs(weekStartOf(today)) - dayMs(weekStartOf(phase.start_date))) / (7 * DAY_MS)) + 1;
}

const phaseWeekCount = (p) => p.weeks || Math.round((dayMs(p.end_date) - dayMs(weekStartOf(p.start_date)) + DAY_MS) / (7 * DAY_MS)) || null;

/**
 * The phases in order, as the page lists them: {...phase, status ('done' |
 * 'now' | 'next' | 'later'), statusWord, datesLabel, weeksLabel, purpose,
 * weekNow ("Week 2 of 4", the current phase only), races (rows inside it)}.
 * A race up to a week after a phase, before the next starts, belongs to it
 * (a race the day after the taper). `outside`: races ahead in no phase.
 */
export function seasonPhases(phases, races, today) {
  const sorted = [...(phases || [])].sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)));
  const nextIdx = sorted.findIndex((p) => dayMs(p.start_date) > dayMs(today));
  // Each race's phase: the one it falls in, else the one that ended up to a
  // week before it (a race the day after the taper, or after the last phase).
  const home = (r) => sorted.find((p) => dayMs(p.start_date) <= dayMs(r.date) && dayMs(r.date) <= dayMs(p.end_date))
    || [...sorted].reverse().find((p) => dayMs(p.end_date) < dayMs(r.date) && dayMs(r.date) <= dayMs(p.end_date) + 7 * DAY_MS) || null;
  const homes = new Map((races || []).filter((r) => r?.date).map((r) => [r, home(r)]));
  const rows = sorted.map((p, i) => {
    const status = dayMs(p.end_date) < dayMs(today) ? 'done'
      : dayMs(p.start_date) <= dayMs(today) ? 'now' : i === nextIdx ? 'next' : 'later';
    const inIt = [...homes].filter(([, h]) => h === p).map(([r]) => r);
    const count = phaseWeekCount(p);
    return {
      ...p, status, statusWord: PHASE_STATUS_WORDS[status],
      datesLabel: windowLabel(p.start_date, p.end_date),
      weeksLabel: count ? `${count} week${count === 1 ? '' : 's'}` : null,
      purpose: p.objective || PHASE_PURPOSE[p.kind] || null,
      weekNow: status === 'now' ? weekOfLabel(phaseWeekNumber(p, today), count) : null,
      races: inIt.sort(byDate).map((r) => raceRow(r, today)),
    };
  });
  const outside = [...homes].filter(([r, h]) => !h && daysBetween(today, r.date) >= 0).map(([r]) => r)
    .sort(byDate).map((r) => raceRow(r, today));
  return { phases: rows, outside };
}
